import type { BoundingRegion } from "@/interface/pdfTypes";
import type { AnnotationConfig, AnnotationStrategy } from "./AnnotationStrategy";

/**
 * Uniform-grid annotation strategy.
 *
 * During extraction each PDF page is overlaid with a `rows × cols` grid and
 * every cell is labelled `<page-letter><cell-number>` — e.g. "B13" is page B,
 * cell 13. A field's `box_ids` name the cells its value falls in. This decoder
 * is the exact inverse of the Python encoder (`PdfGridAnnotator.cell_label` /
 * `get_box_locator` + `CanonicalCaseDataExtractionResult.get_normalized_coordinates`),
 * so it must stay byte-compatible with that geometry.
 *
 * Output polygons use fractional (0..1) page coordinates; the PDF viewer scales
 * them to canvas pixels at render time. `page_number` is 1-based, matching the
 * convention the viewer already consumes.
 */
export class UniformGridStrategy implements AnnotationStrategy {
  readonly type = "uniform_grid";

  /** `PdfGridAnnotator.__init__` defaults — used when a row carries no config. */
  private static readonly DEFAULT_ROWS = 10;
  private static readonly DEFAULT_COLS = 4;

  decode(boxIds: string[], config: AnnotationConfig | null | undefined): BoundingRegion[] {
    const cols = this.positiveInt(config?.cols, UniformGridStrategy.DEFAULT_COLS);
    const rows = this.positiveInt(config?.rows, UniformGridStrategy.DEFAULT_ROWS);

    const regions: BoundingRegion[] = [];
    for (const boxId of boxIds ?? []) {
      const region = this.decodeOne(boxId, rows, cols);
      if (region !== null) {
        regions.push(region);
      }
    }
    return regions;
  }

  private decodeOne(boxId: string, rows: number, cols: number): BoundingRegion | null {
    const locator = this.boxLocator(boxId);
    if (locator === null) {
      return null;
    }
    const { pageNumber, cellIndex } = locator;
    const row = Math.floor((cellIndex - 1) / cols) + 1;
    const col = ((cellIndex - 1) % cols) + 1;
    const xFrac = 1 / cols;
    const yFrac = 1 / rows;
    const x0 = (col - 1) * xFrac;
    const y0 = (row - 1) * yFrac;
    const x1 = col * xFrac;
    const y1 = row * yFrac;
    return {
      page_number: pageNumber,
      polygon: [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: y1 },
        { x: x0, y: y1 },
      ],
    };
  }

  /**
   * Split a box id like "B13" into a 1-based page number (base-26 of the leading
   * letters: A=1, B=2, …, Z=26, AA=27) and a 1-based cell index. Mirrors
   * `PdfGridAnnotator.get_box_locator`. Returns null for malformed ids so the
   * caller can skip them without throwing.
   */
  private boxLocator(boxId: string): { pageNumber: number; cellIndex: number } | null {
    if (!boxId) {
      return null;
    }
    let splitAt = 0;
    while (splitAt < boxId.length && /[A-Za-z]/.test(boxId[splitAt])) {
      splitAt += 1;
    }
    const letters = boxId.slice(0, splitAt).toUpperCase();
    const digits = boxId.slice(splitAt);
    if (letters.length === 0 || digits.length === 0 || !/^\d+$/.test(digits)) {
      return null;
    }
    let pageNumber = 0;
    for (const ch of letters) {
      pageNumber = pageNumber * 26 + (ch.charCodeAt(0) - "A".charCodeAt(0) + 1);
    }
    const cellIndex = parseInt(digits, 10);
    if (cellIndex < 1) {
      return null;
    }
    return { pageNumber, cellIndex };
  }

  private positiveInt(value: unknown, fallback: number): number {
    const n = typeof value === "number" ? value : Number(value);
    return Number.isInteger(n) && n > 0 ? n : fallback;
  }
}
